<template>
  <div class="progress-donut-wrapper">
    <div class="progress-donut">
      <svg viewBox="0 0 200 200" class="donut-svg">
        <circle class="donut-track" cx="100" cy="100" :r="svgRadius" fill="none" />
        <circle
          class="donut-fill"
          cx="100"
          cy="100"
          :r="svgRadius"
          fill="none"
          :stroke-dasharray="dashArray"
          :stroke-dashoffset="dashOffset"
          stroke-linecap="round"
        />
      </svg>
      <div class="donut-label">{{ Math.round(progress) }}%</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import type { ProgressDonutFields } from './definition'

const fields = defineModel<ProgressDonutFields>('fields', {
  required: true,
  default: (): ProgressDonutFields => ({
    duration: 10,
  }),
})

const emit = defineEmits(['onEnd'])

const progress = ref(0)
let animationTimer: ReturnType<typeof setTimeout> | null = null
let completionTimer: ReturnType<typeof setTimeout> | null = null

watch(
  () => fields.value.duration,
  () => {
    // Reset and restart
    progress.value = 0
    if (animationTimer) clearTimeout(animationTimer)
    if (completionTimer) clearTimeout(completionTimer)
    startAnimation()
  },
)

// SVG geometry
const SVG_SIZE = 200
const DEFAULT_STROKE = 16

const strokeWidth = computed(() => fields.value.fillWidth ?? DEFAULT_STROKE)
const svgRadius = computed(() => (SVG_SIZE - strokeWidth.value) / 2)
const circumference = computed(() => svgRadius.value * 2 * Math.PI)
const dashOffset = computed(() => circumference.value / 4)
const dashArray = computed(() => {
  const fillLength = (progress.value / 100) * circumference.value
  const gapLength = circumference.value - fillLength
  return `${fillLength} ${gapLength}`
})

// Generate 20 random steps summing to 100, each between 1-10
function generateSteps(): number[] {
  const stepCount = 20
  const steps: number[] = []
  let remaining = 100

  for (let i = 0; i < stepCount - 1; i++) {
    const maxStep = Math.min(10, remaining - (stepCount - i - 1))
    const minStep = Math.max(1, remaining - 10 * (stepCount - i - 1))
    const step = Math.floor(Math.random() * (maxStep - minStep + 1)) + minStep
    steps.push(step)
    remaining -= step
  }
  steps.push(remaining)

  return steps
}

function startAnimation() {
  const duration = (fields.value.duration ?? 10) * 1000
  const steps = generateSteps()
  const intervalMs = duration / steps.length
  let currentStep = 0
  let accumulated = 0

  function tick() {
    if (currentStep >= steps.length) {
      // 1s pause so user sees 100%, then emit onEnd
      completionTimer = setTimeout(() => {
        emit('onEnd')
      }, 1000)
      return
    }

    accumulated += steps[currentStep]!
    progress.value = Math.min(accumulated, 100)
    currentStep++

    animationTimer = setTimeout(tick, intervalMs)
  }

  tick()
}

onMounted(() => {
  startAnimation()
})

onBeforeUnmount(() => {
  if (animationTimer) clearTimeout(animationTimer)
  if (completionTimer) clearTimeout(completionTimer)
})

// Style overrides
const overrideStrokeWidth = computed(() => `${strokeWidth.value}px`)
const overrideTrackColor = computed(() =>
  getColorStyleData(fields.value.trackColor, 'var(--color-secondary-200)'),
)
const overrideFillColor = computed(() =>
  getColorStyleData(fields.value.fillColor, 'var(--color-secondary-500)'),
)
const overrideLabelSize = computed(() =>
  getSizeStyleData(fields.value.labelSize, 'var(--text-heading-3-size)'),
)
const overrideLabelColor = computed(() =>
  getColorStyleData(fields.value.labelColor, 'var(--color-text-inverse)'),
)
const overrideMargin = computed(() => getSpacingStyleData(fields.value.margin, 'var(--space-24) 0'))
const overrideDiameter = computed(() => getSizeStyleData(fields.value.diameter, '194px'))
</script>

<style scoped lang="scss">
.progress-donut-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: v-bind(overrideMargin);
}

.progress-donut {
  position: relative;
  width: v-bind(overrideDiameter);
  height: v-bind(overrideDiameter);
}

.donut-svg {
  width: 100%;
  height: 100%;
}

.donut-track {
  stroke: v-bind(overrideTrackColor);
  stroke-width: v-bind(overrideStrokeWidth);
}

.donut-fill {
  stroke: v-bind(overrideFillColor);
  stroke-width: v-bind(overrideStrokeWidth);
  transition: stroke-dasharray 0.2s linear;
}

.donut-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: v-bind(overrideLabelSize);
  color: v-bind(overrideLabelColor);
  font-weight: bold;
  user-select: none;
}
</style>
